import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileText, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SectionCard } from '@/components/common/panel';
import { ordersService } from '@/services/orders.service';
import { errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { qk } from '@/lib/query-keys';
import type { MaintenanceEntry } from '@/types/api';
import { MaintenanceAttachments } from './maintenance-attachments';
import { MaintenanceEditDialog } from './maintenance-edit-dialog';
import { MaintenanceDeleteButton } from './maintenance-delete-button';
import { MaintenanceFilePicker, type PendingMaintenance } from './maintenance-file-picker';
import { MaintenanceVisibilityBadge, MaintenanceVisibilitySelect } from './maintenance-visibility';
import type { MaintenanceVisibility } from '@oficina/shared';

interface MaintenanceCardProps {
  orderId?: string;
  entries: MaintenanceEntry[];
  /** OS ainda não salva: registros ficam pendentes e vão junto na criação. */
  pending: PendingMaintenance[];
  onPendingChange: (pending: PendingMaintenance[]) => void;
  locked?: boolean;
  authorName?: string;
  currentUserId?: string;
  isAdmin?: boolean;
}

/**
 * Histórico de serviços executados. Data, hora e usuário são preenchidos automaticamente pelo servidor;
 * numa OS já salva o registro é gravado na hora (como no protótipo).
 */
export function MaintenanceCard({
  orderId,
  entries,
  pending,
  onPendingChange,
  locked,
  authorName,
  currentUserId,
  isAdmin,
}: MaintenanceCardProps) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  // Padrão seguro: interno. A equipe marca explicitamente o que o cliente pode ver.
  const [visibility, setVisibility] = useState<MaintenanceVisibility>('INTERNAL');

  const mutation = useMutation({
    mutationFn: (description: string) => ordersService.addMaintenance(orderId!, description, files, visibility),
    onSuccess: () => {
      setText('');
      setFiles([]);
      setVisibility('INTERNAL');
      void qc.invalidateQueries({ queryKey: qk.orders.detail(orderId!) });
      toast.success('Serviço registrado no histórico.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const add = () => {
    if (mutation.isPending) return;
    const description = text.trim();
    if (description.length < 2) {
      toast.error('Descreva o serviço antes de registrar.');
      return;
    }
    if (orderId) mutation.mutate(description);
    else {
      onPendingChange([...pending, { description, files, visibility }]);
      setText('');
      setFiles([]);
      setVisibility('INTERNAL');
    }
  };

  return (
    <SectionCard
      icon={<FileText />}
      title="Histórico de serviços executados"
      description="Descreva o serviço e anexe fotos ou vídeos. Data, hora e usuário são registrados automaticamente."
    >
      {!locked && (
        <>
          <Label htmlFor="maintenance-text">Serviço realizado</Label>
          <div className="mt-1.5 flex flex-col items-stretch gap-3 sm:flex-row sm:items-start">
            <Textarea
              id="maintenance-text"
              className="min-h-24 flex-1"
              disabled={mutation.isPending}
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={2000}
              placeholder="Troca de tela, limpeza interna, substituição da bateria..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) add();
              }}
            />
            <MaintenanceFilePicker files={files} onChange={setFiles} disabled={mutation.isPending} />
          </div>
          <div className="mt-3">
            <MaintenanceVisibilitySelect
              idPrefix="maintenance-new"
              value={visibility}
              onChange={setVisibility}
              disabled={mutation.isPending}
            />
          </div>
          <Button type="button" className="mt-3" onClick={add} loading={mutation.isPending}>
            <Plus /> Registrar serviço
          </Button>
        </>
      )}
      <ol className="mt-4 space-y-0">
        {pending.map((p, i) => (
          <TimelineItem
            key={`p-${i}`}
            title={`Técnico: ${authorName ?? 'você'}`}
            badge={<MaintenanceVisibilityBadge visibility={p.visibility} />}
            meta="Será registrado ao salvar a OS"
            text={p.description}
            note={p.files.length ? `${p.files.length} anexo(s) selecionado(s)` : undefined}
          />
        ))}
        {entries.map((e) => (
          <TimelineItem
            key={e.id}
            title={`Técnico: ${e.authorName}`}
            badge={<MaintenanceVisibilityBadge visibility={e.visibility} />}
            meta={`Data e hora: ${formatDateTime(e.createdAt)}`}
            text={e.description}
            action={
              orderId ? (
                <div className="flex items-center gap-1">
                  {!locked && currentUserId && e.userId === currentUserId && (
                    <MaintenanceEditDialog orderId={orderId} entry={e} />
                  )}
                  {isAdmin && <MaintenanceDeleteButton orderId={orderId} entry={e} />}
                </div>
              ) : undefined
            }
            children={orderId ? <MaintenanceAttachments orderId={orderId} entry={e} /> : undefined}
          />
        ))}
        {!pending.length && !entries.length && (
          <li className="text-xs text-muted-foreground">Nenhum serviço registrado até o momento.</li>
        )}
      </ol>
    </SectionCard>
  );
}

export function TimelineItem({
  title,
  badge,
  meta,
  text,
  note,
  children,
  action,
}: {
  title: string;
  badge?: React.ReactNode;
  meta?: string;
  text?: string;
  note?: string | null;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <li className="relative ml-[7px] border-l border-[#e5eaf0] pb-3.5 pl-5 text-xs last:border-transparent last:pb-0">
      <span className="absolute -left-[5px] top-[3px] h-[9px] w-[9px] rounded-full bg-primary" aria-hidden />
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <b className="text-[13px]">{title}</b>
          {badge}
        </div>
        {action}
      </div>
      {meta && <small className="text-[#74869b]">{meta}</small>}
      {text && <p className="mt-1.5 whitespace-pre-wrap text-[13px]">{text}</p>}
      {note && <p className="mt-1 text-[#718299]">{note}</p>}
      {children}
    </li>
  );
}
